import { api } from '../utils/api';

/**
 * Event types that can be reported to the server.
 * These are displayed in the "Olay Geçmişi" (Event History) tab.
 */
export type DeviceEventType =
  | 'BOOT_COMPLETE'
  | 'MANIFEST_SYNC'
  | 'CONTENT_DOWNLOAD'
  | 'STORAGE_CLEANUP'
  | 'PLAYBACK_START'
  | 'PLAYBACK_ERROR'
  | 'SCHEDULE_START'
  | 'SCHEDULE_END'
  | 'BLACKOUT_ENTER'
  | 'BLACKOUT_EXIT'
  | 'NETWORK_LOST'
  | 'NETWORK_RESTORED'
  | 'UPDATE_CHECK'
  | 'UPDATE_DOWNLOAD'
  | 'UPDATE_INSTALL'
  | 'MEMORY_PRESSURE_WARNING'
  | 'ERROR'
  | 'WARNING';

interface DeviceEvent {
  type: string;
  data?: Record<string, string>;
  timestamp?: string;
}

const BATCH_SIZE = 10;
const MAX_QUEUE_SIZE = 100;
const FLUSH_INTERVAL_MS = 30000; // Flush every 30 seconds

/**
 * Reports device events to the server for display in the dashboard.
 * Events are queued and sent in batches to minimize network calls.
 */
class EventReporter {
  private eventQueue: DeviceEvent[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private isPaired = false;
  private beforeUnloadHandler: (() => void) | null = null;

  /**
   * Start the event reporter - must be called after device is paired
   */
  start(): void {
    this.isPaired = true;

    // Start periodic flush
    this.flushInterval = setInterval(() => {
      this.flushEvents();
    }, FLUSH_INTERVAL_MS);

    // Report boot/start event
    this.reportBootComplete();

    // Listen for page unload to flush remaining events
    this.beforeUnloadHandler = () => {
      this.flushEventsSync();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    console.log('[EventReporter] Started');
  }

  /**
   * Stop the event reporter
   */
  stop(): void {
    this.isPaired = false;
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    // Remove beforeunload handler
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    // Final flush
    this.flushEvents();
    console.log('[EventReporter] Stopped');
  }

  /**
   * Report a single event (queued for batch sending)
   */
  reportEvent(type: DeviceEventType, data?: Record<string, string>): void {
    if (!this.isPaired) {
      console.debug('[EventReporter] Not paired, skipping event:', type);
      return;
    }

    const event: DeviceEvent = {
      type,
      data,
      timestamp: new Date().toISOString(),
    };

    // Add to queue with size limit
    if (this.eventQueue.length >= MAX_QUEUE_SIZE) {
      this.eventQueue.shift(); // Remove oldest
    }
    this.eventQueue.push(event);

    // Send if batch size reached
    if (this.eventQueue.length >= BATCH_SIZE) {
      this.flushEvents();
    }
  }

  /**
   * Report an important event immediately without batching
   */
  async reportEventImmediate(type: DeviceEventType, data?: Record<string, string>): Promise<void> {
    if (!this.isPaired) {
      console.debug('[EventReporter] Not paired, skipping event:', type);
      return;
    }

    try {
      await api.reportEvent({
        type,
        data,
        timestamp: new Date().toISOString(),
      });
      console.log('[EventReporter] Reported event:', type);
    } catch (error) {
      console.error('[EventReporter] Failed to report event:', type, error);
    }
  }

  /**
   * Flush all queued events to the server
   */
  async flushEvents(): Promise<void> {
    if (this.eventQueue.length === 0 || !this.isPaired) return;

    const eventsToSend = this.eventQueue.splice(0, BATCH_SIZE);

    try {
      await api.reportEventsBatch(eventsToSend);
      console.log('[EventReporter] Flushed', eventsToSend.length, 'events');
    } catch (error) {
      console.error('[EventReporter] Failed to flush events:', error);
      // Re-add failed events to queue (at front)
      this.eventQueue.unshift(...eventsToSend);
    }
  }

  /**
   * Synchronous flush for beforeunload (uses sendBeacon)
   */
  private flushEventsSync(): void {
    if (this.eventQueue.length === 0) return;

    const events = this.eventQueue.splice(0, this.eventQueue.length);

    // Use sendBeacon for reliable delivery during page unload
    try {
      const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
      navigator.sendBeacon('/device/events/batch', blob);
    } catch {
      // Ignore errors during unload
    }
  }

  // Convenience methods for common events

  reportBootComplete(): void {
    this.reportEventImmediate('BOOT_COMPLETE', {
      platform: 'web',
      userAgent: navigator.userAgent,
    });
  }

  reportManifestSync(version: number): void {
    this.reportEventImmediate('MANIFEST_SYNC', { version: version.toString() });
  }

  reportContentDownload(assetId: string, success: boolean): void {
    this.reportEvent('CONTENT_DOWNLOAD', {
      assetId,
      success: success.toString(),
    });
  }

  reportStorageCleanup(deletedCount: number, failedCount: number): void {
    this.reportEvent('STORAGE_CLEANUP', {
      deletedCount: deletedCount.toString(),
      failedCount: failedCount.toString(),
    });
  }

  reportPlaybackStart(playlistId: string, assetId: string, scheduleRuleId?: string): void {
    const data: Record<string, string> = { playlistId, assetId };
    if (scheduleRuleId) data.scheduleRuleId = scheduleRuleId;
    this.reportEvent('PLAYBACK_START', data);
  }

  reportPlaybackError(error: string, assetId?: string): void {
    this.reportEventImmediate('PLAYBACK_ERROR', {
      error,
      assetId: assetId || '',
    });
  }

  reportScheduleStart(playlistId: string, scheduleRuleId?: string): void {
    const data: Record<string, string> = { playlistId };
    if (scheduleRuleId) data.scheduleRuleId = scheduleRuleId;
    this.reportEventImmediate('SCHEDULE_START', data);
  }

  reportScheduleEnd(): void {
    this.reportEventImmediate('SCHEDULE_END');
  }

  reportBlackoutEnter(): void {
    this.reportEventImmediate('BLACKOUT_ENTER');
  }

  reportBlackoutExit(): void {
    this.reportEventImmediate('BLACKOUT_EXIT');
  }

  reportNetworkLost(): void {
    this.reportEventImmediate('NETWORK_LOST');
  }

  reportNetworkRestored(): void {
    this.reportEventImmediate('NETWORK_RESTORED');
  }

  reportError(error: string, context?: string): void {
    this.reportEventImmediate('ERROR', {
      error,
      context: context || '',
    });
  }

  reportWarning(warning: string, context?: string): void {
    this.reportEvent('WARNING', {
      warning,
      context: context || '',
    });
  }

  // One-shot per session — emitted from analytics.ts when JS heap ratio
  // crosses 75%. Report-only signal for dashboard trending; no client-side
  // action is triggered. Caller is responsible for the one-shot guard.
  reportMemoryPressure(payload: {
    ratio: number;
    jsHeapUsedMb: number;
    jsHeapLimitMb: number;
  }): void {
    this.reportEventImmediate('MEMORY_PRESSURE_WARNING', {
      ratio: payload.ratio.toFixed(3),
      jsHeapUsedMb: payload.jsHeapUsedMb.toString(),
      jsHeapLimitMb: payload.jsHeapLimitMb.toString(),
    });
  }
}

export const eventReporter = new EventReporter();
